/**
 * Campaign API Client — "Rival Trainer Road"
 *
 * Mirrors the structure of [squadPvpApi.ts]. Campaign battles are created
 * here, then played through the existing squad battle endpoints
 * (submitSquadMove / getSquadBattle / abandonSquadBattle in squadPvpApi.ts).
 */

import type { SoulShardGift } from '@shared/domain/suikamonTracks';
import { API_BASE_URL } from '../config/apiConfig';
import type { ApiSquadBattleState } from './squadPvpApi';
import type {
  CampaignNodeKind,
  CampaignReward,
  NodeDialogue,
  RivalEncounterSummary,
} from '@shared/domain/campaignConfig';
import { getSession } from '../auth/sessionStore';
import { logClientError } from './clientLogApi';

const API_VERSION = 'v1';
const API_BASE_PATH = '/api';
const JSON_HEADERS = { 'Content-Type': 'application/json' } as const;

function withBase(path: string): string {
  return `${API_BASE_URL}${API_BASE_PATH}/${API_VERSION}${path}`;
}

async function parseJson<T>(response: Response): Promise<T> {
  return (await response.json()) as T;
}

/** localStorage key for the player's campaign squad selection (separate from ranked). */
export const CAMPAIGN_SELECTED_PETS_KEY = 'suikamon_campaign_selected_pet_ids';

/**
 * localStorage flag: the player has seen the cinematic intro ("The Last Souls").
 * Bump the version suffix to re-show the intro after a major story rewrite
 * (v4: the intro became a six-panel wordless comic).
 */
export const CAMPAIGN_INTRO_SEEN_KEY = 'suikamon_campaign_intro_seen_v4';

/**
 * Router state that makes `/campaign` play its opening even when it was seen before. A hatch
 * navigates with it: meeting a new Suikamon hands straight over to the story.
 */
export interface CampaignRouteState {
  playIntro?: boolean;
}
export const CAMPAIGN_PLAY_INTRO_STATE: CampaignRouteState = { playIntro: true };

/** localStorage flag prefix: the player has seen a Tale's intro comic (`…<worldId>`). */
/** localStorage flag prefix: the player has seen a Tale's midpoint comic (`…<worldId>`). */
export const CAMPAIGN_TALE_MID_SEEN_PREFIX = 'suikamon_campaign_tale_mid_seen_';

export const CAMPAIGN_TALE_INTRO_SEEN_PREFIX = 'suikamon_campaign_tale_intro_seen_';

/** localStorage flag prefix: the player has seen the chapter of a Tale's opening told after a battle (`…<nodeId>`). */
export const CAMPAIGN_TALE_CHAPTER_SEEN_PREFIX = 'suikamon_campaign_tale_chapter_seen_';

/**
 * localStorage flag: the player has seen the one-time "Battle Pills Unlocked!"
 * milestone (shown after clearing World 2, when pills turn on from World 3). The
 * address is appended so a shared browser shows it once PER account.
 */
export const CAMPAIGN_PILL_UNLOCK_SEEN_KEY = 'suikamon_campaign_pill_unlock_seen';

/**
 * localStorage handoff: the campaign battle the player just left. The battle
 * page writes it on exit so the campaign page can reopen that node's world map
 * (step 2) instead of the storyline overview (step 1) — a one-shot marker,
 * cleared as soon as the campaign page consumes it, so it only ever redirects
 * the return-from-battle visit, never a normal navigation.
 *
 * The value is a JSON CampaignReturnMarker. A bare nodeId string is still
 * accepted (see parseCampaignReturnMarker) so a marker written by the previous
 * build, or by a tab left open across a deploy, is not silently dropped.
 */
export const CAMPAIGN_RETURN_NODE_KEY = 'suikamon_campaign_return_node';

export interface CampaignReturnMarker {
  nodeId: string;
  /**
   * The node had already been cleared before this battle — i.e. a replay, not
   * progress. Distinguishes "just finished the story" from "revisited a story
   * finished long ago", which must not trigger the next-story handoff.
   */
  alreadyCleared: boolean;
}

/** Read the marker in either format; null when absent or unusable. */
export function parseCampaignReturnMarker(raw: string | null): CampaignReturnMarker | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && typeof (parsed as CampaignReturnMarker).nodeId === 'string') {
      const marker = parsed as CampaignReturnMarker;
      return { nodeId: marker.nodeId, alreadyCleared: marker.alreadyCleared === true };
    }
  } catch {
    /* Legacy plain-string marker — fall through. */
  }
  // A bare nodeId carries no replay information; treat it as first-time progress,
  // which is what it always meant before the flag existed.
  return typeof raw === 'string' && raw.length > 0 ? { nodeId: raw, alreadyCleared: false } : null;
}

// ============================================================================
// Responses
// ============================================================================

export interface CampaignProgressResponse {
  success: boolean;
  clearedNodeIds?: string[];
  /** Per-rival battle memory (Rival Memory) — drives dialogue variants + rematch previews. */
  encounters?: RivalEncounterSummary[];
  error?: string;
}

export interface DailyRivalStatusResponse {
  success: boolean;
  /** Today's challenge node, or null until the player's first campaign clear. */
  nodeId?: string | null;
  /** True once today's bonus was claimed (win or loss). */
  claimed?: boolean;
  won?: boolean;
  /** Reward constants from the backend so the UI never hardcodes them. */
  xpWin?: number;
  xpLoss?: number;
  error?: string;
}

export interface CampaignBattleNodeInfo {
  nodeId: string;
  kind: CampaignNodeKind;
  rivalName: string;
  rivalTitle: string;
  rivalId: string;
  dialogue: NodeDialogue;
  xpReward: number;
  playerLevelCap: number;
}

export interface CreateCampaignBattleResponse {
  success: boolean;
  battleId?: string;
  battle?: ApiSquadBattleState;
  node?: CampaignBattleNodeInfo;
  alreadyCleared?: boolean;
  activeBattleId?: string;
  /** Node of the unfinished battle (may differ from the requested node). */
  activeNodeId?: string | null;
  error?: string;
  /**
   * True on a 403 when a guest (free-tier) player requested a node past the free
   * boundary — the frontend should raise FreeCapModal (reason='campaign') instead
   * of surfacing this as a generic error.
   */
  requiresAdoption?: boolean;
}

// ============================================================================
// API calls
// ============================================================================

export async function fetchCampaignProgress(
  walletAddress: string,
  options?: {
    /**
     * Default true: send the viewer's session token so the backend resolves
     * progress by their account (all linked wallets). Pass false when viewing
     * ANOTHER trainer's public profile — otherwise a logged-in viewer would
     * see their own progress instead of that trainer's (same rule as
     * fetchOwnerPets).
     */
    asSelf?: boolean;
  }
): Promise<CampaignProgressResponse> {
  try {
    const asSelf = options?.asSelf ?? true;
    const token = asSelf ? getSession()?.token : undefined;
    const response = await fetch(withBase(`/campaign/progress/${walletAddress}`), {
      headers: {
        'Cache-Control': 'no-cache',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    return parseJson<CampaignProgressResponse>(response);
  } catch (error) {
    console.error('Failed to fetch campaign progress:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Network error' };
  }
}

export async function fetchDailyRival(
  walletAddress: string
): Promise<DailyRivalStatusResponse> {
  try {
    const token = getSession()?.token;
    const response = await fetch(withBase(`/campaign/daily-rival/${walletAddress}`), {
      headers: {
        'Cache-Control': 'no-cache',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    return parseJson<DailyRivalStatusResponse>(response);
  } catch (error) {
    // `fetch` rejects with a TypeError only when the request never reached the server — a tab
    // waking up, a network switch, a backend restart mid-deploy. React Query refetches this on
    // window focus and retries on its own, so that is a warning, not a bug report.
    const log = error instanceof TypeError ? console.warn : console.error;
    log('Failed to fetch daily rival:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Network error' };
  }
}

export interface CampaignRewardClaimResponse {
  success: boolean;
  nodeId?: string;
  /** True when THIS call opened the gift and paid out. */
  claimed?: boolean;
  /** True when the gift had already been opened before (no re-grant). */
  alreadyClaimed?: boolean;
  /** The node's authored reward (what to celebrate), regardless of claim state. */
  reward?: CampaignReward;
  /** How many owned pets received the Suika/XP part. */
  petsAffected?: number;
  /**
   * The gift's Soul Shards (`tracks` in code) as paid: `balance` when kept toward the species'
   * egg, `suika` when the player already owns that Suikamon and they became Suika instead.
   */
  tracks?: SoulShardGift;
  /** True on a 403 when a guest requested a gift past the free boundary. */
  requiresAdoption?: boolean;
  error?: string;
}

/**
 * Open a reward-only GIFT step (no battle). First-time-only on the backend — a
 * repeat call returns alreadyClaimed with no re-grant.
 */
export async function claimCampaignReward(
  walletAddress: string,
  nodeId: string
): Promise<CampaignRewardClaimResponse> {
  try {
    const token = getSession()?.token;
    const response = await fetch(withBase(`/campaign/nodes/${nodeId}/claim`), {
      method: 'POST',
      headers: {
        ...JSON_HEADERS,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ walletAddress }),
    });
    return parseJson<CampaignRewardClaimResponse>(response);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Network error';
    console.error('Failed to claim campaign gift:', error);
    logClientError(`[campaign-gift-claim] ${message} (node=${nodeId})`, 'claimCampaignReward');
    return { success: false, error: message };
  }
}

export async function createCampaignBattle(
  walletAddress: string,
  nodeId: string,
  petIds: string[]
): Promise<CreateCampaignBattleResponse> {
  try {
    const token = getSession()?.token;
    const response = await fetch(withBase('/campaign/battles/create'), {
      method: 'POST',
      headers: {
        ...JSON_HEADERS,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ walletAddress, nodeId, petIds }),
    });
    return parseJson<CreateCampaignBattleResponse>(response);
  } catch (error) {
    // Transport-level failure — the request never got a parseable response, so the backend
    // has NO log of it. Forward it so battle-start failures are visible server-side too.
    const message = error instanceof Error ? error.message : 'Network error';
    console.error('Failed to create campaign battle:', error);
    logClientError(`[campaign-battle-create] ${message} (node=${nodeId})`, 'createCampaignBattle');
    return { success: false, error: message };
  }
}

export interface DailyLadderResponse {
  success: boolean;
  /** Campaign wins banked against the current UTC day. */
  winsToday: number;
  /** What the ladder has already paid today, in Suika. */
  suikaToday: number;
  /** UTC midnight, computed SERVER-side — a device clock never decides when a reward resets. */
  resetsAt: string;
  error?: string;
}

/**
 * Where the player sits on today's win ladder (paid in Suika).
 *
 * Deliberately returns POSITION ONLY. The rungs themselves are `DAILY_WIN_DROP_TIERS` in
 * `@shared/domain/cardShopConfig`, which the page imports directly, so the ladder is described
 * in exactly one place and the screen cannot drift from what the database actually pays.
 */
export async function fetchDailyLadder(walletAddress: string): Promise<DailyLadderResponse | null> {
  try {
    const token = getSession()?.token;
    const response = await fetch(withBase(`/campaign/daily-ladder/${walletAddress}`), {
      headers: {
        ...JSON_HEADERS,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    return parseJson<DailyLadderResponse>(response);
  } catch (error) {
    console.error('Failed to fetch daily ladder:', error);
    return null;
  }
}
